import PropTypes from "prop-types";
import { useState } from "react";
import { useSnackbar } from "notistack";
// material
import {
  Card,
  Stack,
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  MenuItem,
  Chip,
  Alert,
  useTheme,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
// hooks
import useAuth from "../../../hooks/useAuth";
// utils
import {
  CheckPerm,
  ORDER_STATUS,
  CANCELLATION_REASONS,
  STORE_PICKUP_STAGES,
  HOME_DELIVERY_STAGES,
  getNextOrderStage,
} from "../../../utils/apis";

// ----------------------------------------------------------------------

OrderProgressStepper.propTypes = {
  invoice: PropTypes.object.isRequired,
  fetch: PropTypes.func,
};

export default function OrderProgressStepper({ invoice, fetch }) {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const { sendOrderUpdate } = useAuth();
  const hasOrderPermission = CheckPerm("accept_orders");

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [cancellationReason, setCancellationReason] = useState("");
  const [stageNotes, setStageNotes] = useState("");

  const currentStatus = parseInt(invoice.status, 10);
  const isPickup = Boolean(invoice.isPickup);
  const stages = isPickup ? STORE_PICKUP_STAGES : HOME_DELIVERY_STAGES;

  const currentStatusMeta =
    ORDER_STATUS.find((s) => parseInt(s.value, 10) === currentStatus) || {};

  const nextStageInfo = getNextOrderStage(currentStatus, isPickup);

  // Compute active step index for Stepper
  let activeStep = 0;
  if (currentStatus === 0) {
    activeStep = -1; // Not yet accepted
  } else if (currentStatus === 5) {
    activeStep = stages.length; // All completed
  } else {
    const idx = stages.indexOf(currentStatus);
    activeStep = idx >= 0 ? idx : 0;
  }

  const isCancelled = currentStatus === 2 || currentStatus === 6 || currentStatus === 7;
  const isCompleted = currentStatus === 5;

  const handleAdvanceStage = async () => {
    if (!nextStageInfo || nextStageInfo.targetStatus === null) return;
    try {
      setLoading(true);
      await sendOrderUpdate(
        invoice.orderId,
        nextStageInfo.targetStatus,
        stageNotes || nextStageInfo.actionLabel
      );
      enqueueSnackbar(`Order updated to ${nextStageInfo.actionLabel}!`, {
        variant: "success",
      });
      setConfirmOpen(false);
      setStageNotes("");
      if (fetch) fetch();
    } catch (err) {
      enqueueSnackbar(err.message, { variant: "error" });
    }
    setLoading(false);
  };

  const handleCancelOrder = async () => {
    if (!cancellationReason) {
      enqueueSnackbar("Please select a cancellation reason.", {
        variant: "error",
      });
      return;
    }
    try {
      setLoading(true);
      await sendOrderUpdate(invoice.orderId, 2, cancellationReason);
      enqueueSnackbar("Order cancelled successfully.", {
        variant: "info",
      });
      setCancelOpen(false);
      setCancellationReason("");
      if (fetch) fetch();
    } catch (err) {
      enqueueSnackbar(err.message, { variant: "error" });
    }
    setLoading(false);
  };

  return (
    <Card
      variant="outlined"
      sx={{
        mb: 3,
        p: { xs: 2.5, md: 3 },
        border: "0.4px solid",
        borderColor: "background.border",
        borderRadius: "8px",
        boxShadow:
          "4px 2px 124px rgba(46, 53, 58, 0.02), 0px 4px 124px rgba(46, 41, 78, 0.02)",
      }}
    >
      {/* Header */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={1.5}
        sx={{ mb: 3 }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          {isPickup ? (
            <StorefrontOutlinedIcon color="primary" sx={{ fontSize: 24 }} />
          ) : (
            <LocalShippingOutlinedIcon color="info" sx={{ fontSize: 24 }} />
          )}
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {isPickup ? "Store Pick-up Fulfillment Track" : "Home Delivery Fulfillment Track"}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip
            size="small"
            label={currentStatusMeta.label || "Status"}
            color={currentStatusMeta.color || "default"}
            sx={{ fontWeight: 700, textTransform: "uppercase" }}
          />
        </Stack>
      </Stack>

      {/* Stepper Pipeline */}
      {!isCancelled ? (
        <Box sx={{ width: "100%", overflowX: "auto", py: 1 }}>
          <Stepper
            activeStep={activeStep}
            alternativeLabel
            sx={{
              minWidth: { xs: 580, md: "auto" },
              "& .MuiStepLabel-label": {
                fontSize: { xs: "0.75rem", md: "0.8125rem" },
                fontWeight: 600,
                mt: 1,
              },
              "& .MuiStepIcon-root.Mui-active": {
                color: theme.palette.primary.main,
              },
              "& .MuiStepIcon-root.Mui-completed": {
                color: theme.palette.success.main,
              },
            }}
          >
            {stages.map((stageVal, index) => {
              const meta =
                ORDER_STATUS.find((s) => parseInt(s.value, 10) === stageVal) || {};
              const label = meta.label || `Stage ${stageVal}`;
              return (
                <Step key={stageVal} completed={activeStep > index}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              );
            })}
          </Stepper>
        </Box>
      ) : (
        <Alert severity="error" sx={{ mb: 2 }}>
          This order has been cancelled/rejected. No further stage progression can be performed.
        </Alert>
      )}

      {/* Stage Actions Bar */}
      {hasOrderPermission && !isCancelled && !isCompleted && (
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", sm: "center" }}
          spacing={2}
          sx={{
            mt: 3,
            pt: 2.5,
            borderTop: "1px dashed",
            borderColor: "divider",
          }}
        >
          {currentStatus === 0 ? (
            <Alert severity="warning" sx={{ width: { xs: "100%", sm: "auto" }, py: 0 }}>
              Order is pending acceptance. Please accept to initiate fulfillment.
            </Alert>
          ) : (
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Current Stage:{" "}
              <strong>{currentStatusMeta.label || "In Progress"}</strong>
            </Typography>
          )}

          <Stack direction="row" spacing={1.5} justifyContent="flex-end">
            <Button
              variant="outlined"
              color="error"
              size="small"
              startIcon={<CancelOutlinedIcon />}
              onClick={() => setCancelOpen(true)}
            >
              Cancel Order
            </Button>

            {nextStageInfo && (
              <Button
                variant="contained"
                color={currentStatus === 0 ? "success" : "primary"}
                size="small"
                endIcon={<ArrowForwardIcon />}
                onClick={() => setConfirmOpen(true)}
              >
                {nextStageInfo.actionLabel}
              </Button>
            )}
          </Stack>
        </Stack>
      )}

      {isCompleted && (
        <Alert
          severity="success"
          icon={<CheckCircleIcon fontSize="inherit" />}
          sx={{ mt: 2.5 }}
        >
          Order has been fulfilled and marked as completed. All stages concluded.
        </Alert>
      )}

      {/* Advance Confirmation Dialog */}
      <Dialog
        open={confirmOpen}
        onClose={() => !loading && setConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {nextStageInfo?.dialogTitle || "Advance Order Stage"}
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <DialogContentText sx={{ mb: 2 }}>
            {nextStageInfo?.dialogDescription}
          </DialogContentText>
          <TextField
            fullWidth
            label="Optional Note / Tracking Info"
            placeholder="e.g. Courier tracking code, rider contact, package location..."
            value={stageNotes}
            onChange={(e) => setStageNotes(e.target.value)}
            multiline
            rows={2}
            size="small"
            variant="outlined"
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmOpen(false)}
            color="inherit"
            disabled={loading}
          >
            Cancel
          </Button>
          <LoadingButton
            variant="contained"
            color="primary"
            loading={loading}
            onClick={handleAdvanceStage}
          >
            Confirm & Update
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* Cancel Order Dialog */}
      <Dialog
        open={cancelOpen}
        onClose={() => !loading && setCancelOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Cancel Order</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <DialogContentText sx={{ mb: 2 }}>
            Please confirm you want to cancel this order and specify the reason:
          </DialogContentText>
          <TextField
            select
            fullWidth
            label="Cancellation Reason"
            value={cancellationReason}
            onChange={(e) => setCancellationReason(e.target.value)}
            size="small"
            sx={{ mb: 2 }}
          >
            {CANCELLATION_REASONS.map((reason) => (
              <MenuItem key={reason} value={reason}>
                {reason}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setCancelOpen(false)}
            color="inherit"
            disabled={loading}
          >
            Back
          </Button>
          <LoadingButton
            variant="contained"
            color="error"
            loading={loading}
            onClick={handleCancelOrder}
          >
            Cancel Order
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Card>
  );
}
